'use es6';

import { createSelector } from 'reselect';
import SeamlessImmutable from 'seamless-immutable';
import { getPath } from 'DesignData/lib/objectSelectors';
import GlobalWidget from 'DesignData/models/GlobalWidget';
import * as SmartWidgetTypes from 'DesignData/enums/SmartWidgetTypes';
import { getTargetType } from 'DesignData/helpers/smartObjectHelper';
import { SMART_CONTENT } from 'DesignData/constants/Scopes';
import { isScoped as getIsScoped } from 'DesignData/selectors/auth';
import { getCustomWidgetsByType, getCustomWidgetsData } from 'DesignData/selectors/customWidgets';

const getGlobalWidgetId = (state, props) => props.assetId || props.globalWidgetId;

export const getGlobalWidgetsById = state => state.globalWidgets.byId;
export const getGlobalWidgetSummariesByName = state => state.globalWidgets.summaries.widgetsByName;

const getFetchErrorById = state => state.globalWidgets.fetchErrorById;

export const getGlobalWidget = (state, props) => {
  const globalWidgetId = getGlobalWidgetId(state, props);
  const {
    revisionId
  } = props;

  if (revisionId) {
    const revision = getPath(state, ['revisions', 'byId', globalWidgetId, 'revisions', revisionId]);
    return revision ? new GlobalWidget(revision.object) : null;
  }

  const byId = getGlobalWidgetsById(state);
  return byId.getIn([globalWidgetId, 'present']) || null;
};
export const getCatalogWidgetForGlobalWidget = createSelector([getGlobalWidget, getCustomWidgetsByType], (globalWidget, customWidgetsByType) => {
  if (!globalWidget || !globalWidget.type) {
    return null;
  }

  return customWidgetsByType[globalWidget.type];
});
export const getCustomWidgetForGlobalWidget = createSelector([getGlobalWidget, getCustomWidgetsData], (globalWidget, customWidgetsById) => {
  if (!globalWidget || !globalWidget.type) {
    return null;
  }

  if (globalWidget.generatedCustomWidgetId) {
    return customWidgetsById[globalWidget.generatedCustomWidgetId] || null;
  }

  const customWidgetId = Object.keys(customWidgetsById).find(_customWidgetId => {
    const _customWidget = customWidgetsById[_customWidgetId];
    return _customWidget.name === globalWidget.name;
  });
  return customWidgetId ? customWidgetsById[customWidgetId] : null;
});

function isValidGlobalWidgetState(state) {
  return !!state;
}

export const makeGetPastEditHistoryCount = () => {
  return createSelector([getGlobalWidgetId, getGlobalWidgetsById], (globalWidgetId, byId) => {
    const past = byId.getIn([globalWidgetId, 'past']);
    return past ? past.filter(isValidGlobalWidgetState).length : 0;
  });
};
export const makeGetFutureEditHistoryCount = () => {
  return createSelector([getGlobalWidgetId, getGlobalWidgetsById], (globalWidgetId, byId) => {
    const future = byId.getIn([globalWidgetId, 'future']);
    return future ? future.filter(isValidGlobalWidgetState).length : 0;
  });
};
export const makeGetWidgetSmartContentProps = () => {
  const defaultState = SeamlessImmutable({
    isEligibleForSmartContent: false,
    hasSmartContent: false,
    smartDefinitionId: null,
    smartTargetType: null,
    smartType: null
  });
  let state = defaultState;
  return createSelector([getGlobalWidget, getIsScoped], (globalWidget, isScoped) => {
    if (!(globalWidget && isScoped(SMART_CONTENT))) {
      return defaultState;
    }

    const {
      definition_id: smartDefinitionId,
      smart_objects: smartObjects,
      smart_type: smartType
    } = globalWidget.getSmartProperties();
    state = state.merge({
      isEligibleForSmartContent: !!SmartWidgetTypes[globalWidget.type],
      hasSmartContent: !!(smartType && smartObjects && smartObjects.length),
      smartDefinitionId,
      smartTargetType: getTargetType(globalWidget.type),
      smartType
    });
    return state;
  });
};
export const getFetchError = createSelector([getGlobalWidgetId, getFetchErrorById], (globalWidgetId, fetchErrorById) => fetchErrorById[globalWidgetId]);